import React, { useCallback, useEffect, useMemo } from 'react';
import { Input, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { Button, Colors, Text, Checkbox } from '@mercator-gcp/grout';
import { useForm } from 'react-hook-form';
import Dropdown from 'components/elements/Dropdown';
import DropdownItem from 'components/elements/Dropdown/DropdownItem';
import InfoMarker from 'components/elements/InfoMarker';
import { DollarIcon } from 'components/icons/Dollar';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import {
  useDashboardCostIncludeUnknownState,
  useDashboardCostState,
} from 'state/dashboard';
import {
  textInputToFormattedIntegerMiddleware,
  removeCommas,
  formatNumberWithCommas,
} from 'utils/formMiddleware';

const menuContainerStyle = {
  rowGap: 'padding_12',
};

const inputStyle = {
  border: '2px solid #EAECEA',
  borderRadius: '4px',
};

const clearAllText = {
  paddingTop: 'padding_12',
};
const includeUnknownValueStyle = {
  columnGap: 'padding_4',
};

const ProjectValue = () => {
  const [savedCost, setSavedCost] = useDashboardCostState();
  const [costIncludeUnknown, setCostIncludeUnknown] =
    useDashboardCostIncludeUnknownState();

  const { handleSubmit, register, reset } = useForm({
    mode: 'onChange',
    defaultValues: {
      minPrice: Number(savedCost.min) > 0 ? formatNumberWithCommas(savedCost.min) : '',
      maxPrice: Number(savedCost.max) > 0 ? formatNumberWithCommas(savedCost.max) : '',
    },
  });

  const filterCount = useMemo(() => {
    let count = 0;

    if (Number(savedCost.min) > 0) {
      count += 1;
    }

    if (Number(savedCost.max) > 0) {
      count += 1;
    }

    return count;
  }, [savedCost.min, savedCost.max]);

  const onClear = useCallback(() => {
    setSavedCost({ min: 0, max: 0 });
    reset({ minPrice: '', maxPrice: '' });
  }, [reset, setSavedCost]);

  const onSubmit = useCallback(
    (data: { minPrice?: string; maxPrice?: string }) => {
      const minValue = data.minPrice ? Number(removeCommas(String(data.minPrice))) : 0;
      const maxValue = data.maxPrice ? Number(removeCommas(String(data.maxPrice))) : 0;
      setSavedCost({ min: minValue, max: maxValue });
    },
    [setSavedCost],
  );

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSubmit(onSubmit)();
    }
  };

  useEffect(() => {
    // if saved cost changes reset form
    reset({
      minPrice: Number(savedCost.min) > 0 ? formatNumberWithCommas(savedCost.min) : '',
      maxPrice: Number(savedCost.max) > 0 ? formatNumberWithCommas(savedCost.max) : '',
    });
  }, [savedCost, reset]);

  return (
    <Dropdown title="Total Project Value" filterCount={filterCount}>
      <DropdownItem>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FlexColumn sx={menuContainerStyle}>
            <Text.Small color={Colors.TextQuiet}>
              This filter will only surface projects with a value assigned from the
              building permit
            </Text.Small>
            <FlexColumn>
              <Text.SmallLabel>Min</Text.SmallLabel>
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none">
                  <DollarIcon />
                </InputLeftElement>
                <Input
                  size="md"
                  sx={inputStyle}
                  border="1px solid"
                  focusBorderColor="brand.green"
                  errorBorderColor="brand.red"
                  {...register('minPrice', {
                    onChange: textInputToFormattedIntegerMiddleware,
                  })}
                  onKeyPress={handleKeyPress}
                  onBlur={() => handleSubmit(onSubmit)()}
                  type="text"
                />
              </InputGroup>
            </FlexColumn>

            <FlexColumn>
              <Text.SmallLabel>Max</Text.SmallLabel>
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none">
                  <DollarIcon />
                </InputLeftElement>
                <Input
                  sx={inputStyle}
                  border="1px solid"
                  focusBorderColor="brand.green"
                  errorBorderColor="brand.red"
                  {...register('maxPrice', {
                    onChange: textInputToFormattedIntegerMiddleware,
                  })}
                  onKeyPress={handleKeyPress}
                  onBlur={() => handleSubmit(onSubmit)()}
                  type="text"
                />
              </InputGroup>
            </FlexColumn>
            <Checkbox
              isChecked={costIncludeUnknown}
              onToggle={() => {
                setCostIncludeUnknown(!costIncludeUnknown);
              }}
            >
              <FlexRow sx={includeUnknownValueStyle}>
                <Text>Include unknown values</Text>
                <InfoMarker label="Data availability varies by city. We recommend including unknown values to broaden search results when using multiple filters." />
              </FlexRow>
            </Checkbox>
          </FlexColumn>
          <FlexRow>
            <Button variant="unstyled" onClick={onClear}>
              <Text.Small color={Colors.Tangerine900} sx={clearAllText}>
                Clear all
              </Text.Small>
            </Button>
          </FlexRow>
        </form>
      </DropdownItem>
    </Dropdown>
  );
};

export default ProjectValue;
